import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { Info, Loader2, RotateCcw, XCircle } from "lucide-react";

type Status = {
  origem: "stripe" | "voucher" | "nenhuma";
  plano: string;
  periodo: string;
  diasDesdeCompra: number | null;
  feedbacksUsados: number;
  limiteFeedbacks: number;
  janelaDias: number;
  podeReembolsar: boolean;
  motivoReembolso: string;
  cancelAtPeriodEnd: boolean;
  fimPeriodo: string | null;
  voucherPresente: boolean;
  podeCancelar: boolean;
};

interface PlanActionsProps {
  onChanged?: () => void;
}

const PlanActions = ({ onChanged }: PlanActionsProps) => {
  const { toast } = useToast();
  const [status, setStatus] = useState<Status | null>(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState<"refund" | "cancel" | null>(null);
  const [confirm, setConfirm] = useState<"refund" | "cancel" | null>(null);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase.functions.invoke("manage-subscription", {
      body: { action: "status" },
    });
    if (!error && data) setStatus(data as Status);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const run = async (action: "refund" | "cancel") => {
    setConfirm(null);
    setWorking(action);
    const { data, error } = await supabase.functions.invoke("manage-subscription", { body: { action } });
    setWorking(null);
    const errMsg = (data as any)?.error || error?.message;
    if (errMsg) {
      toast({ title: "Não foi possível concluir", description: errMsg, variant: "destructive" });
    } else {
      toast({
        title: action === "refund" ? "Reembolso solicitado" : "Cancelamento confirmado",
        description: (data as any)?.mensagem,
      });
      onChanged?.();
    }
    load();
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" /> Verificando assinatura...
      </div>
    );
  }

  if (!status || status.origem === "nenhuma") return null;

  return (
    <div className="space-y-3">
      {status.origem === "stripe" && (
        <>
          {status.podeReembolsar ? (
            <Button
              variant="destructive"
              className="w-full"
              onClick={() => setConfirm("refund")}
              disabled={working !== null}
            >
              {working === "refund" ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RotateCcw className="w-4 h-4 mr-2" />
              )}
              Pedir reembolso
            </Button>
          ) : (
            <Alert>
              <Info className="w-4 h-4" />
              <AlertDescription className="text-xs leading-relaxed">{status.motivoReembolso}</AlertDescription>
            </Alert>
          )}
        </>
      )}

      {status.cancelAtPeriodEnd ? (
        <Alert>
          <Info className="w-4 h-4" />
          <AlertDescription className="text-xs">
            Cancelamento agendado. Seu acesso segue ativo até{" "}
            {status.fimPeriodo ? new Date(status.fimPeriodo).toLocaleDateString("pt-BR") : "o fim do período pago"} e
            não haverá nova cobrança.
          </AlertDescription>
        </Alert>
      ) : (
        status.podeCancelar && (
          <Button variant="outline" className="w-full" onClick={() => setConfirm("cancel")} disabled={working !== null}>
            {working === "cancel" ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <XCircle className="w-4 h-4 mr-2" />
            )}
            Cancelar plano
          </Button>
        )
      )}

      <AlertDialog open={confirm !== null} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm === "refund" ? "Confirmar pedido de reembolso?" : "Confirmar cancelamento?"}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              {confirm === "refund" ? (
                <>
                  <span className="block">
                    Esta ação é <strong>irreversível</strong>: a assinatura é encerrada na hora e sua conta volta ao
                    plano Start, perdendo os recursos do plano pago.
                  </span>
                  <span className="block">
                    O estorno é enviado automaticamente à Stripe e processado em até <strong>10 dias úteis</strong>. Em
                    cartão de crédito, o crédito pode aparecer em até 2 faturas.
                  </span>
                </>
              ) : status.origem === "voucher" ? (
                <>
                  <span className="block">
                    Esta ação é <strong>irreversível</strong>: seu acesso ao plano atual é encerrado e a conta volta ao
                    plano Start.
                  </span>
                  <span className="block">
                    {status.voucherPresente
                      ? "Este plano veio de um presente gratuito enviado por e-mail — ao cancelar, o benefício é perdido."
                      : "Se o voucher foi obtido por pagamento presencial ou direto, procure o fornecedor/consultor responsável pela venda para tratar de eventual reembolso."}
                  </span>
                </>
              ) : (
                <>
                  <span className="block">
                    Esta ação é <strong>irreversível</strong>. O mês em curso já foi pago e continua disponível até{" "}
                    {status.fimPeriodo ? new Date(status.fimPeriodo).toLocaleDateString("pt-BR") : "o fim do período"};
                    a renovação não acontecerá.
                  </span>
                  <span className="block">
                    A Stripe registra o cancelamento imediatamente.
                    {status.periodo === "annual"
                      ? " Como sua compra foi anual, estornamos automaticamente o valor proporcional aos meses não utilizados (processado em até 10 dias úteis)."
                      : ""}
                  </span>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Voltar</AlertDialogCancel>
            <AlertDialogAction onClick={() => confirm && run(confirm)}>
              {confirm === "refund" ? "Sim, pedir reembolso" : "Sim, cancelar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PlanActions;
